import { useRef } from "react";
import BottomSheet from "../../../../components/ui/BottomSheet";
import SheetHeader from "../../../../components/ui/SheetHeader";
import { IStep, IStepOption } from "../../../../classes/interfaces";
import { useIsDesktopViewport } from "../../../../hooks/useIsDesktopViewport";
import StepGroupHeader from "./StepGroupHeader";
import OptionRow from "./OptionRow";
import ChoiceOptionRow from "./ChoiceOptionRow";
import { stepSubtitle } from "./stepSubtitle";
import { Scroll, StepBody, OptionsCard, ChoiceList, Footer, ConfirmButton } from "./StepProductSheet.styles";

interface StepProductSheetProps {
  isOpen: boolean;
  steps: IStep[];
  onMoreOption: (step: IStep, option: IStepOption) => void;
  onLessOption: (step: IStep, option: IStepOption) => void;
  onClose: () => void;
  onConfirm: () => void;
}

export default function StepProductSheet({ isOpen, steps, onMoreOption, onLessOption, onClose, onConfirm }: StepProductSheetProps) {
  const desktop = useIsDesktopViewport();
  const scrollRef = useRef<HTMLDivElement>(null);
  const stepRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const reqCount = steps.filter((s) => s.minimum > 0).length;
  const doneCount = steps.filter((s) => s.minimum > 0 && s.selected >= s.minimum).length;

  function scrollToStep(index: number) {
    const step = steps[index];
    const el = step ? stepRefs.current[step.id] : null;
    const sc = scrollRef.current;
    if (!el || !sc) return;
    const top = el.getBoundingClientRect().top - sc.getBoundingClientRect().top + sc.scrollTop - 4;
    sc.scrollTo({ top, behavior: "smooth" });
  }

  function pickSingle(index: number, step: IStep, option: IStepOption) {
    onMoreOption(step, option);
    // let the option's checked-state re-render before measuring the next step's scroll offset
    setTimeout(() => scrollToStep(index + 1), 80);
  }

  return (
    <BottomSheet open={isOpen} onClose={onClose} maxHeight="94%" centered={desktop}>
      <SheetHeader
        title="Monte seu produto"
        sub={reqCount > 0 ? `${doneCount} de ${reqCount} escolhas obrigatórias` : "Personalize do seu jeito"}
        onClose={onClose}
      />
      <Scroll ref={scrollRef}>
        {steps.map((step, i) => (
          <div key={step.id} ref={(el) => (stepRefs.current[step.id] = el)}>
            <StepGroupHeader
              index={i + 1}
              title={step.name}
              subtitle={stepSubtitle(step)}
              required={step.minimum > 0}
              done={step.selected >= step.minimum}
            />
            <StepBody>
              {step.maximum === 1 ? (
                <ChoiceList>
                  {step.options.map((option) => (
                    <ChoiceOptionRow
                      key={option.id}
                      name={option.name}
                      price={option.price}
                      checked={option.amount === 1}
                      onClick={() => pickSingle(i, step, option)}
                    />
                  ))}
                </ChoiceList>
              ) : (
                <OptionsCard>
                  {step.options.map((option) => (
                    <OptionRow
                      key={option.id}
                      name={option.name}
                      price={option.price}
                      qty={option.amount}
                      max={option.maximum > 0 ? option.maximum : 9}
                      onChange={(v) => (v > (option.amount || 0) ? onMoreOption(step, option) : onLessOption(step, option))}
                    />
                  ))}
                </OptionsCard>
              )}
            </StepBody>
          </div>
        ))}
      </Scroll>
      <Footer>
        <ConfirmButton onClick={onConfirm}>Confirmar</ConfirmButton>
      </Footer>
    </BottomSheet>
  );
}
